<template>
    <div class="classifications" v-if="$can('part_view')">
        <v-snackbar v-model="snackbar" :timeout="4000" bottom right :color="color">
            <span>{{ message }}</span>
        </v-snackbar>

        <!-- Bulk Classification Dialog -->
        <v-dialog v-model="bulkDialog" max-width="600">
            <v-card>
                <v-card-title>
                    Bulk Classify Items
                    <v-spacer></v-spacer>
                    <v-btn icon @click="bulkDialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text>
                    <p class="mb-4">
                        Assign classifications to <strong>{{ selectedParts.length }}</strong> selected items.
                        Leave fields empty to skip that classification type.
                    </p>

                    <v-divider class="mb-4"></v-divider>

                    <!-- Category Assignment -->
                    <div class="subtitle-1 font-weight-bold mb-2">Category</div>
                    <v-row>
                        <v-col cols="6">
                            <v-select
                                v-model="bulkCategory"
                                :items="formData.categories"
                                item-text="name"
                                item-value="id"
                                label="Category"
                                outlined
                                dense
                                clearable
                                @change="onBulkCategoryChange"
                            ></v-select>
                        </v-col>
                        <v-col cols="6">
                            <v-select
                                v-model="bulkSubCategory"
                                :items="bulkSubCategories"
                                item-text="name"
                                item-value="id"
                                label="Sub-Category"
                                outlined
                                dense
                                clearable
                                :disabled="!bulkCategory"
                            ></v-select>
                        </v-col>
                    </v-row>

                    <v-divider class="my-4"></v-divider>

                    <!-- Level Assignment -->
                    <div class="subtitle-1 font-weight-bold mb-2">Grade Level</div>
                    <v-row>
                        <v-col cols="6">
                            <v-select
                                v-model="bulkLevel"
                                :items="formData.levels"
                                item-text="name"
                                item-value="id"
                                label="Level"
                                outlined
                                dense
                                clearable
                                @change="onBulkLevelChange"
                            ></v-select>
                        </v-col>
                        <v-col cols="6">
                            <v-select
                                v-model="bulkSubLevel"
                                :items="bulkSubLevels"
                                item-text="name"
                                item-value="id"
                                label="Grade"
                                outlined
                                dense
                                clearable
                                :disabled="!bulkLevel"
                            ></v-select>
                        </v-col>
                    </v-row>

                    <v-divider class="my-4"></v-divider>

                    <!-- Learning Area Assignment -->
                    <div class="subtitle-1 font-weight-bold mb-2">Learning Area / Subject</div>
                    <v-select
                        v-model="bulkLearningArea"
                        :items="formData.learningAreas"
                        item-text="name"
                        item-value="id"
                        label="Learning Area"
                        outlined
                        dense
                        clearable
                    ></v-select>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="bulkDialog = false">Cancel</v-btn>
                    <v-btn
                        color="primary"
                        :loading="bulkLoading"
                        :disabled="!bulkCategory && !bulkLevel && !bulkLearningArea"
                        @click="applyBulkClassification"
                    >
                        Apply Classification
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <div class="pa-5">
            <v-layout column>
                <v-card elevation="0">
                    <v-card-title>
                        <v-icon left color="primary">mdi-tag-multiple</v-icon>
                        Item Classification Management
                    </v-card-title>
                    <v-card-subtitle>
                        Classify bookshop items by categories, grade levels, and learning areas
                    </v-card-subtitle>

                    <!-- Filter Section -->
                    <v-card-text>
                        <v-expansion-panels flat>
                            <v-expansion-panel>
                                <v-expansion-panel-header>
                                    <v-icon left>mdi-filter</v-icon>
                                    Filter Items
                                </v-expansion-panel-header>
                                <v-expansion-panel-content>
                                    <v-row>
                                        <v-col cols="12" md="3">
                                            <v-select
                                                v-model="filterCategory"
                                                :items="formData.categories"
                                                item-text="name"
                                                item-value="id"
                                                label="Category"
                                                outlined
                                                dense
                                                clearable
                                                @change="onFilterCategoryChange"
                                            ></v-select>
                                        </v-col>
                                        <v-col cols="12" md="3">
                                            <v-select
                                                v-model="filterSubCategory"
                                                :items="filterSubCategories"
                                                item-text="name"
                                                item-value="id"
                                                label="Sub-Category"
                                                outlined
                                                dense
                                                clearable
                                                :disabled="!filterCategory"
                                            ></v-select>
                                        </v-col>
                                        <v-col cols="12" md="3">
                                            <v-select
                                                v-model="filterLevel"
                                                :items="formData.levels"
                                                item-text="name"
                                                item-value="id"
                                                label="Level"
                                                outlined
                                                dense
                                                clearable
                                                @change="onFilterLevelChange"
                                            ></v-select>
                                        </v-col>
                                        <v-col cols="12" md="3">
                                            <v-select
                                                v-model="filterSubLevel"
                                                :items="filterSubLevels"
                                                item-text="name"
                                                item-value="id"
                                                label="Grade"
                                                outlined
                                                dense
                                                clearable
                                                :disabled="!filterLevel"
                                            ></v-select>
                                        </v-col>
                                    </v-row>
                                    <v-row>
                                        <v-col cols="12" md="4">
                                            <v-select
                                                v-model="filterLearningArea"
                                                :items="formData.learningAreas"
                                                item-text="name"
                                                item-value="id"
                                                label="Learning Area"
                                                outlined
                                                dense
                                                clearable
                                            ></v-select>
                                        </v-col>
                                        <v-col cols="12" md="4">
                                            <v-text-field
                                                v-model="searchTerm"
                                                label="Search by name or serial"
                                                outlined
                                                dense
                                                clearable
                                                @keyup.enter="applyFilters"
                                            ></v-text-field>
                                        </v-col>
                                        <v-col cols="12" md="4">
                                            <v-btn color="primary" @click="applyFilters" class="mr-2">
                                                <v-icon left>mdi-magnify</v-icon>
                                                Search
                                            </v-btn>
                                            <v-btn outlined @click="clearFilters">
                                                <v-icon left>mdi-close</v-icon>
                                                Clear
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </v-expansion-panel-content>
                            </v-expansion-panel>
                        </v-expansion-panels>

                        <!-- Quick Filter Chips -->
                        <div class="mt-4">
                            <v-chip
                                class="mr-2 mb-2"
                                :color="showUnclassified === 'any' ? 'error' : ''"
                                :outlined="showUnclassified !== 'any'"
                                @click="toggleUnclassified('any')"
                            >
                                <v-icon left small>mdi-alert</v-icon>
                                Unclassified Items
                            </v-chip>
                            <v-chip
                                class="mr-2 mb-2"
                                :color="showUnclassified === 'category' ? 'warning' : ''"
                                :outlined="showUnclassified !== 'category'"
                                @click="toggleUnclassified('category')"
                            >
                                Missing Category
                            </v-chip>
                            <v-chip
                                class="mr-2 mb-2"
                                :color="showUnclassified === 'level' ? 'warning' : ''"
                                :outlined="showUnclassified !== 'level'"
                                @click="toggleUnclassified('level')"
                            >
                                Missing Level
                            </v-chip>
                            <v-chip
                                class="mr-2 mb-2"
                                :color="showUnclassified === 'learning_area' ? 'warning' : ''"
                                :outlined="showUnclassified !== 'learning_area'"
                                @click="toggleUnclassified('learning_area')"
                            >
                                Missing Learning Area
                            </v-chip>
                        </div>
                    </v-card-text>

                    <v-divider></v-divider>

                    <!-- Bulk Actions -->
                    <v-card-text v-if="selectedParts.length > 0">
                        <v-alert type="info" dense>
                            <v-row align="center">
                                <v-col>
                                    {{ selectedParts.length }} items selected
                                </v-col>
                                <v-col class="text-right">
                                    <v-btn small color="primary" @click="openBulkDialog">
                                        <v-icon left small>mdi-tag-plus</v-icon>
                                        Bulk Classify
                                    </v-btn>
                                    <v-btn small text @click="selectedParts = []" class="ml-2">
                                        Clear Selection
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-alert>
                    </v-card-text>

                    <!-- Items Table -->
                    <v-card-text>
                        <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>

                        <v-data-table
                            v-model="selectedParts"
                            :headers="headers"
                            :items="parts"
                            show-select
                            item-key="id"
                            :loading="loading"
                            class="elevation-1"
                        >
                            <template v-slot:item.name="{ item }">
                                <div class="d-flex align-center">
                                    <v-avatar size="40" class="mr-2">
                                        <v-img
                                            v-if="item.image"
                                            :src="path + '/storage/part_pictures/' + item.image"
                                        ></v-img>
                                        <v-img v-else src="img/book-silhouette-vector-14.png"></v-img>
                                    </v-avatar>
                                    <div>
                                        <div class="font-weight-medium">{{ item.name }}</div>
                                        <div class="caption grey--text">{{ item.serial_no }}</div>
                                    </div>
                                </div>
                            </template>

                            <template v-slot:item.categories="{ item }">
                                <div v-if="item.category_parts && item.category_parts.length > 0">
                                    <v-chip
                                        v-for="cp in item.category_parts"
                                        :key="cp.id"
                                        x-small
                                        class="mr-1 mb-1"
                                        color="blue"
                                        text-color="white"
                                    >
                                        {{ cp.sub_category?.name || cp.category?.name }}
                                    </v-chip>
                                </div>
                                <v-chip v-else x-small color="grey" outlined>None</v-chip>
                            </template>

                            <template v-slot:item.levels="{ item }">
                                <div v-if="item.level_parts && item.level_parts.length > 0">
                                    <v-chip
                                        v-for="lp in item.level_parts"
                                        :key="lp.id"
                                        x-small
                                        class="mr-1 mb-1"
                                        color="green"
                                        text-color="white"
                                    >
                                        {{ lp.sub_level?.name }}
                                    </v-chip>
                                </div>
                                <v-chip v-else x-small color="grey" outlined>None</v-chip>
                            </template>

                            <template v-slot:item.learning_areas="{ item }">
                                <div v-if="item.learning_area_parts && item.learning_area_parts.length > 0">
                                    <v-chip
                                        v-for="lap in item.learning_area_parts"
                                        :key="lap.id"
                                        x-small
                                        class="mr-1 mb-1"
                                        color="purple"
                                        text-color="white"
                                    >
                                        {{ lap.learning_area?.name }}
                                    </v-chip>
                                </div>
                                <v-chip v-else x-small color="grey" outlined>None</v-chip>
                            </template>

                            <template v-slot:item.price="{ item }">
                                <span v-if="item.price">KES {{ item.price | formatNumber }}</span>
                                <span v-else class="grey--text">-</span>
                            </template>

                            <template v-slot:item.actions="{ item }">
                                <v-btn
                                    icon
                                    small
                                    @click="goToPartAdmin(item)"
                                    color="primary"
                                >
                                    <v-icon small>mdi-pencil</v-icon>
                                </v-btn>
                            </template>
                        </v-data-table>

                        <v-pagination
                            v-if="pagination.total > pagination.per_page"
                            :length="Math.ceil(pagination.total / pagination.per_page)"
                            v-model="pagination.current_page"
                            @input="loadParts"
                            class="mt-4"
                        ></v-pagination>
                    </v-card-text>
                </v-card>
            </v-layout>
        </div>
    </div>
</template>

<script>
import apiCall from '@/utils/api'

export default {
    name: 'ClassificationAdmin',
    data() {
        return {
            snackbar: false,
            message: '',
            color: '',
            loading: false,
            path: process.env.VUE_APP_API_URL,

            // Data
            parts: [],
            pagination: {
                current_page: 1,
                per_page: 20,
                total: 0,
            },
            formData: {
                categories: [],
                levels: [],
                learningAreas: [],
            },

            // Selection
            selectedParts: [],

            // Filters
            filterCategory: null,
            filterSubCategory: null,
            filterLevel: null,
            filterSubLevel: null,
            filterLearningArea: null,
            searchTerm: '',
            showUnclassified: null,

            // Bulk Dialog
            bulkDialog: false,
            bulkLoading: false,
            bulkCategory: null,
            bulkSubCategory: null,
            bulkLevel: null,
            bulkSubLevel: null,
            bulkLearningArea: null,

            headers: [
                { text: 'Item', value: 'name', width: '25%' },
                { text: 'Categories', value: 'categories', sortable: false },
                { text: 'Grade Levels', value: 'levels', sortable: false },
                { text: 'Learning Areas', value: 'learning_areas', sortable: false },
                { text: 'Price', value: 'price', align: 'right' },
                { text: 'Actions', value: 'actions', sortable: false, align: 'center' },
            ],
        }
    },
    computed: {
        filterSubCategories() {
            if (!this.filterCategory) return []
            const cat = this.formData.categories.find(c => c.id === this.filterCategory)
            return cat?.sub_categories || []
        },
        filterSubLevels() {
            if (!this.filterLevel) return []
            const level = this.formData.levels.find(l => l.id === this.filterLevel)
            return level?.sub_levels || []
        },
        bulkSubCategories() {
            if (!this.bulkCategory) return []
            const cat = this.formData.categories.find(c => c.id === this.bulkCategory)
            return cat?.sub_categories || []
        },
        bulkSubLevels() {
            if (!this.bulkLevel) return []
            const level = this.formData.levels.find(l => l.id === this.bulkLevel)
            return level?.sub_levels || []
        },
    },
    methods: {
        async initialize() {
            await this.loadFormData()
            await this.loadParts()
        },
        async loadFormData() {
            try {
                const response = await apiCall({
                    url: '/api/part?type=formData',
                    method: 'GET'
                })
                this.formData = response
            } catch (error) {
                this.showSnackbar('Failed to load form data', 'error')
            }
        },
        async loadParts() {
            this.loading = true
            try {
                let url = '/api/parts/filter?page=' + this.pagination.current_page

                if (this.filterCategory) url += `&category_id=${this.filterCategory}`
                if (this.filterSubCategory) url += `&sub_category_id=${this.filterSubCategory}`
                if (this.filterLevel) url += `&level_id=${this.filterLevel}`
                if (this.filterSubLevel) url += `&sub_level_id=${this.filterSubLevel}`
                if (this.filterLearningArea) url += `&learning_area_id=${this.filterLearningArea}`
                if (this.searchTerm) url += `&search=${this.searchTerm}`

                const response = await apiCall({ url, method: 'GET' })
                this.parts = response.data
                this.pagination.current_page = response.current_page
                this.pagination.total = response.total
                this.pagination.per_page = response.per_page
            } catch (error) {
                this.showSnackbar('Failed to load items', 'error')
            }
            this.loading = false
        },
        async loadUnclassifiedParts(type) {
            this.loading = true
            try {
                const response = await apiCall({
                    url: `/api/parts/unclassified?missing=${type}&page=${this.pagination.current_page}`,
                    method: 'GET'
                })
                this.parts = response.data
                this.pagination.current_page = response.current_page
                this.pagination.total = response.total
                this.pagination.per_page = response.per_page
            } catch (error) {
                this.showSnackbar('Failed to load unclassified items', 'error')
            }
            this.loading = false
        },
        onFilterCategoryChange() {
            this.filterSubCategory = null
        },
        onFilterLevelChange() {
            this.filterSubLevel = null
        },
        onBulkCategoryChange() {
            this.bulkSubCategory = null
        },
        onBulkLevelChange() {
            this.bulkSubLevel = null
        },
        applyFilters() {
            this.showUnclassified = null
            this.pagination.current_page = 1
            this.loadParts()
        },
        clearFilters() {
            this.filterCategory = null
            this.filterSubCategory = null
            this.filterLevel = null
            this.filterSubLevel = null
            this.filterLearningArea = null
            this.searchTerm = ''
            this.showUnclassified = null
            this.pagination.current_page = 1
            this.loadParts()
        },
        toggleUnclassified(type) {
            if (this.showUnclassified === type) {
                this.showUnclassified = null
                this.loadParts()
            } else {
                this.showUnclassified = type
                this.pagination.current_page = 1
                this.loadUnclassifiedParts(type)
            }
        },
        openBulkDialog() {
            this.bulkCategory = null
            this.bulkSubCategory = null
            this.bulkLevel = null
            this.bulkSubLevel = null
            this.bulkLearningArea = null
            this.bulkDialog = true
        },
        async applyBulkClassification() {
            this.bulkLoading = true
            try {
                const data = {
                    part_ids: this.selectedParts.map(p => p.id),
                }

                if (this.bulkCategory && this.bulkSubCategory) {
                    data.category_id = this.bulkCategory
                    data.sub_category_id = this.bulkSubCategory
                }
                if (this.bulkLevel && this.bulkSubLevel) {
                    data.level_id = this.bulkLevel
                    data.sub_level_id = this.bulkSubLevel
                }
                if (this.bulkLearningArea) {
                    data.learning_area_id = this.bulkLearningArea
                }

                const response = await apiCall({
                    url: '/api/parts/bulk-classify',
                    method: 'POST',
                    data
                })

                if (response.status === 'success') {
                    this.showSnackbar(response.message, 'success')
                    this.bulkDialog = false
                    this.selectedParts = []
                    await this.loadParts()
                } else {
                    this.showSnackbar(response.message || 'Classification failed', 'error')
                }
            } catch (error) {
                this.showSnackbar('Failed to apply classification', 'error')
            }
            this.bulkLoading = false
        },
        goToPartAdmin(item) {
            this.$router.push({
                path: '/parts-admin',
                query: { part: item.slug }
            })
        },
        showSnackbar(message, color) {
            this.message = message
            this.color = color
            this.snackbar = true
        },
    },
    mounted() {
        this.initialize()
    },
}
</script>

<style scoped>
.v-data-table {
    border: 1px solid #e0e0e0;
}
</style>
